import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { FirebaseError } from 'firebase/app';
import toast from 'react-hot-toast';
import { castDanceOffVote, getDanceOff, subscribeToMyDanceOffVote, type DanceOff } from '../../services/danceOff';

interface DanceOffVoteModalProps {
  danceOff: DanceOff;
  attendeeId: string;
  onClose: () => void;
}

export default function DanceOffVoteModal({ danceOff, attendeeId, onClose }: DanceOffVoteModalProps) {
  const [savedVote, setSavedVote] = useState<number | null>(null);
  const [pendingVote, setPendingVote] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToMyDanceOffVote(danceOff.id, attendeeId, (vote) => {
      setSavedVote(vote?.contestant ?? null);
    });
    return () => unsubscribe();
  }, [danceOff.id, attendeeId]);

  const handleVote = async (contestant: number) => {
    if (pendingVote !== null) return;
    setPendingVote(contestant);
    setMessage(null);
    try {
      await castDanceOffVote(danceOff.id, danceOff.eventId, attendeeId, contestant);
    } catch (error) {
      if (error instanceof FirebaseError && error.code === 'permission-denied') {
        try {
          const fresh = await getDanceOff(danceOff.id);
          if (fresh && fresh.status !== 'open') {
            setMessage('Voting just closed');
            toast.error('Voting closed before your vote counted');
          } else {
            setMessage("Couldn't save your vote");
          }
        } catch (rereadError) {
          console.error('[DanceOff] Re-reading after a refused vote failed:', rereadError);
          setMessage("Couldn't save your vote");
        }
      } else {
        console.error('[DanceOff] Vote failed:', error);
        setMessage("Couldn't save your vote");
        toast.error("Couldn't save your vote");
      }
    } finally {
      setPendingVote(null);
    }
  };

  const shownVote = pendingVote ?? savedVote;
  const contestants = Array.from({ length: danceOff.contestantCount }, (_, index) => index + 1);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 border border-transparent dark:border-gray-600 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-pink-600 dark:text-pink-400">💃 {danceOff.title}</p>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Vote for your favorite dancer</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {contestants.map((contestant) => {
            const selected = shownVote === contestant;
            return (
              <button
                key={contestant}
                onClick={() => handleVote(contestant)}
                disabled={pendingVote !== null}
                aria-pressed={selected}
                aria-label={`Vote for contestant ${contestant}`}
                className={`min-h-[64px] rounded-xl text-3xl font-bold transition-colors disabled:cursor-wait ${
                  selected
                    ? 'bg-pink-500 text-white ring-4 ring-pink-300 dark:ring-pink-700'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-pink-100 dark:hover:bg-pink-900/30'
                }`}
              >
                {contestant}
              </button>
            );
          })}
        </div>

        <p className="mt-4 text-sm text-center text-gray-600 dark:text-gray-400 min-h-[1.25rem]">
          {shownVote !== null ? `Your vote: ${shownVote} — you can change it until voting closes` : 'Tap a number to vote'}
        </p>
        {message && (
          <p role="alert" className="mt-2 text-sm text-center font-medium text-red-600 dark:text-red-400">{message}</p>
        )}
      </div>
    </div>
  );
}
